import { fabric } from 'fabric';
import { useEffect, useMemo, useState } from 'react';

import { generateCanvasImage } from '../utils';

import {
  CANVAS_OBJECT_TYPE_PREFIX,
  CORNER_RADIUS_SIGN,
} from '~/core/constants';
import { drawingTool } from '~/core/engine/DrawingTool';
import { ShapeType } from '~/core/enums';
import { useSelectedColor } from '~/core/hooks/useSelectedColor';
import { DrawingShape } from '~/core/models';
import { drawSideItems } from '~/core/utils/drawingTool/drawingTool.utils';
import {
  useDrawingShapeContext,
  useSlabImgContext,
} from '~/legacy/easy-estimator-page/context';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { selectNeedRedrawCanvas } from '~/redux/slices/drawingShape';
import { selectFabricCanvas } from '~/redux/slices/fabric';
import theme from '~/theme/theme';

const {
  drawingShape: { stroke },
} = theme.light.drawingTool;

const objectTypesPrefixes = [
  CANVAS_OBJECT_TYPE_PREFIX.CORNER_RADIUS_ARC,
  CANVAS_OBJECT_TYPE_PREFIX.BUMP_OUT,
  CANVAS_OBJECT_TYPE_PREFIX.RADIUS_OUT,
] as const satisfies Readonly<string[]>;

const emptyArray = [] satisfies DrawingShape[];

const isFabricTextObject = (
  object: fabric.Object
): object is fabric.Text | fabric.IText =>
  object instanceof fabric.Text || object instanceof fabric.IText;

/**Mutates canvas argument, removes selections */
const removeSelections = (
  canvas: fabric.Canvas,
  overlayColor: string
): void => {
  // calling selectShape with activeDrawingShape undefined makes a canvas with no shapes selected.
  drawingTool.selectShape({
    allShapes: emptyArray,
    canvas,
    activeDrawingShape: undefined,
    color: overlayColor,
  });
  // Removes selection circles
  drawingTool.removeObjectByName(
    canvas,
    CANVAS_OBJECT_TYPE_PREFIX.SELECTION_CIRCLE
  );
  canvas.getObjects().forEach((object) => {
    if (
      isFabricTextObject(object) &&
      object.name?.includes(CANVAS_OBJECT_TYPE_PREFIX.SHAPE_MEASURE_LABEL) &&
      object.text?.length === 1 &&
      !object.text.includes(CORNER_RADIUS_SIGN)
    ) {
      object.set({
        text: '',
      });
    } else if (
      object.name?.includes(CANVAS_OBJECT_TYPE_PREFIX.SHAPE_MEASURE_LABEL)
    ) {
      object.set({
        stroke: stroke.normal,
        fill: stroke.normal,
      });
    }

    /**Case where object is a group */
    if ((object as fabric.Group).getObjects) {
      const objects = (object as fabric.Group).getObjects();

      objects.forEach((object) => {
        const shouldTurnStrokeGray = objectTypesPrefixes.some((prefix) =>
          object?.name?.includes(prefix)
        );

        if (shouldTurnStrokeGray) {
          object.set({
            stroke: stroke.normal,
          });
        }
      });
    }
  });
};

export const cloneCanvasAndRemoveSelections = (
  canvas: fabric.Canvas,
  overlayColor?: string
): Promise<fabric.Canvas> =>
  new Promise((resolve) => {
    canvas?.clone?.(
      (canvasClone: fabric.Canvas) => {
        if (overlayColor) {
          removeSelections(canvasClone, overlayColor);
        }
        resolve(canvasClone);
      },
      ['name', 'data'] //Without this array FabricJs drops this values
    );
  });

/**
 * Provides a canvas clone with the selected shape removed
 * @param stopUrlGeneration stops the url generation for performance in components that are hidden instead of unmounted
 */
export const useCanvasClone = () => {
  const dispatch = useAppDispatch();
  const canvas = useAppSelector(selectFabricCanvas);
  const { selectedValue: selectedColor } = useSelectedColor();
  const needRedrawCanvas = useAppSelector(selectNeedRedrawCanvas);
  const {
    getters: { drawingShapes },
  } = useDrawingShapeContext();
  const slabImg = useSlabImgContext();

  const [canvasClone, setCanvasClone] = useState<Readonly<fabric.Canvas>>();

  /**
   * A JPEG URL of the canvas with no shapes selected
   */
  const canvasJpegUrl = useMemo(
    () => (!canvasClone ? null : generateCanvasImage('jpeg', canvasClone)),
    [canvasClone]
  );

  useEffect(() => {
    if (
      selectedColor &&
      drawingTool.allShapesAndAppliancesAreDrawn(canvas, drawingShapes)
    ) {
      // Check if shapes have valid fabricGroup references on the canvas
      // Faux shapes and shapes without fabricGroup are skipped
      const shapesHaveFabricGroups = drawingShapes.every(
        (shape) =>
          shape.type === ShapeType.FAUX_SHAPE ||
          !shape.fabricGroup ||
          canvas.contains(shape.fabricGroup)
      );

      // Draw side items on the ORIGINAL canvas before cloning
      // This ensures side items (backsplash, waterfall) appear with correct color
      // even if the Design page wasn't visited after color change
      // drawRectangleSideItem removes existing side items first, so this is idempotent
      if (shapesHaveFabricGroups) {
        drawSideItems({
          drawingShapes,
          selectedColor,
          canvas,
          activeDrawingShape: undefined,
          // No-op - dimensions not editable in canvas clone for Order Summary
          onDimensionEdit: () => undefined,
        });

        // Apply stone pattern fill to all shapes including side items
        // useOverlay: false since we don't need the dragging overlay for clone
        if (slabImg.element) {
          drawingTool.fillColor(
            canvas,
            selectedColor,
            drawingShapes,
            false,
            slabImg.element
          );
        }
      }

      canvas?.clone?.(
        (canvasClone: fabric.Canvas) => {
          if (selectedColor.overlayColor) {
            removeSelections(canvasClone, selectedColor.overlayColor);
          }
          setCanvasClone(canvasClone);
        },
        ['name', 'data'] //Without this array FabricJs drops this values
      );
    }
  }, [
    canvas,
    drawingShapes,
    needRedrawCanvas,
    selectedColor,
    dispatch,
    slabImg,
  ]);

  return { canvasJpegUrl };
};
